import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Download, RefreshCw, Package, Calendar, User, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const BundleHistory = () => {
  const navigate = useNavigate();
  const [boxes, setBoxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [exporting, setExporting] = useState(false);
  
  // Search/filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  const fetchBoxes = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('session_token');
      
      // Build query params
      const params = new URLSearchParams();
      if (searchTerm) params.append('slip_number', searchTerm);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      const res = await fetch(`${API}/api/wms/packing/all-boxes?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to fetch boxes');
      }
      
      const data = await res.json();
      setBoxes(data.boxes || []);
    } catch (error) {
      console.error('Error fetching boxes:', error);
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, dateFrom, dateTo]);

  useEffect(() => {
    fetchBoxes();
  }, [fetchBoxes]);

  const handleDownloadSlip = async (box) => {
    const { box_id, box_number, source } = box;
    try {
      setDownloading(box_id);
      const token = localStorage.getItem('session_token');
      // Direct-ship (inhouse) boxes use a different endpoint keyed by box_number
      const url = source === 'inhouse_ship'
        ? `${API}/api/assortment-ops/ship/box/${encodeURIComponent(box_number)}/packing-slip`
        : `${API}/api/wms/packing/box/${box_id}/slip-pdf`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to download slip');
      const blob = await res.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `packing_slip_${box_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(objectUrl);

      toast.success(`Downloaded slip for ${box_number}`);
    } catch (error) {
      console.error('Error downloading slip:', error);
      toast.error('Failed to download packing slip');
    } finally {
      setDownloading(null);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchBoxes();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setDateFrom('');
    setDateTo('');
  };

  const handleExportCSV = () => {
    if (boxes.length === 0) {
      toast.error('No data to export');
      return;
    }

    setExporting(true);
    try {
      // Define CSV headers
      const headers = [
        'Box Number',
        'Source',
        'Pick Slip',
        'Customer',
        'Items Count',
        'Total Qty',
        'Packed By',
        'Packed At',
        'Status'
      ];

      // Build CSV rows
      const rows = boxes.map(box => [
        box.box_number || '',
        box.source === 'inhouse_ship' ? 'Direct Ship' : 'Packing',
        box.pick_slip_number || '',
        box.customer || '',
        box.items?.length || 0,
        box.total_qty || 0,
        box.packer_name || '',
        box.packed_at ? new Date(box.packed_at).toLocaleString('en-IN') : '',
        box.status || 'packed'
      ]);

      // Create CSV content
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.href = url;
      
      // Generate filename with date range if filtered
      let filename = 'bundle_history';
      if (dateFrom || dateTo) {
        filename += `_${dateFrom || 'start'}_to_${dateTo || 'end'}`;
      }
      filename += `_${new Date().toISOString().split('T')[0]}.csv`;
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Exported ${boxes.length} records to CSV`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    try {
      const dt = new Date(isoString);
      return dt.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return isoString.slice(0, 10);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/wms/fg/packing')}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
              data-testid="bundle-history-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold font-display">Bundle History</h1>
              <p className="text-sm text-[#E8DCC8]">Search and reprint bundle slips</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={handleExportCSV}
              disabled={boxes.length === 0 || exporting}
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="bundle-history-export-btn"
            >
              {exporting ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Download className="w-5 h-5 mr-2" />
                  Export
                </>
              )}
            </Button>
            <Button
              variant="ghost"
              onClick={fetchBoxes}
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="bundle-history-refresh-btn"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {/* Search and Filter Section */}
        <div className="bg-white rounded-lg border border-[#E5DFD0] p-4 mb-6">
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Search by slip/box number */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[#5A5F6E] mb-1">
                  Search by Slip/Box Number
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#5A5F6E]" />
                  <Input
                    type="text"
                    placeholder="Enter pick slip or box number..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="bundle-history-search-input"
                  />
                </div>
              </div>
              
              {/* Date From */}
              <div>
                <label className="block text-sm font-medium text-[#5A5F6E] mb-1">
                  From Date
                </label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  data-testid="bundle-history-date-from"
                />
              </div>
              
              {/* Date To */}
              <div>
                <label className="block text-sm font-medium text-[#5A5F6E] mb-1">
                  To Date
                </label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  data-testid="bundle-history-date-to"
                />
              </div>
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={clearFilters}
                data-testid="bundle-history-clear-btn"
              >
                Clear Filters
              </Button>
              <Button
                type="submit"
                className="bg-[#2D5F4F] hover:bg-[#234a3d]"
                data-testid="bundle-history-search-btn"
              >
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
            </div>
          </form>
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-[#2D5F4F]" />
            <span className="ml-2 text-[#5A5F6E]">Loading boxes...</span>
          </div>
        ) : boxes.length === 0 ? (
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-12 text-center">
            <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-[#1A1F2E] mb-2">No Boxes Found</h3>
            <p className="text-[#5A5F6E]">
              {searchTerm || dateFrom || dateTo 
                ? "No boxes match your search criteria. Try adjusting filters."
                : "No packing boxes have been created yet."}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-[#E5DFD0] overflow-hidden">
            <div className="px-4 py-3 bg-[#F8F4EE] border-b border-[#E5DFD0]">
              <h2 className="font-semibold text-[#1A1F2E]">
                Found {boxes.length} Box{boxes.length !== 1 ? 'es' : ''}
              </h2>
            </div>
            
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F8F4EE] text-[#5A5F6E]">
                  <tr>
                    <th className="px-4 py-3 text-left">Box Number</th>
                    <th className="px-4 py-3 text-left">Pick Slip</th>
                    <th className="px-4 py-3 text-left">Customer</th>
                    <th className="px-4 py-3 text-center">Items</th>
                    <th className="px-4 py-3 text-center">Qty</th>
                    <th className="px-4 py-3 text-left">Packed By</th>
                    <th className="px-4 py-3 text-left">Packed At</th>
                    <th className="px-4 py-3 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5DFD0]">
                  {boxes.map((box, idx) => (
                    <tr key={`${box.source}-${box.box_id}-${idx}`} className="hover:bg-[#FFFBF5]" data-testid={`bundle-row-${box.box_id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-[#1A1F2E]">{box.box_number}</span>
                          {box.source === 'inhouse_ship' && (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-700 border border-indigo-200"
                              title={`Inhouse Direct Ship (${box.destination_type || ''})`}
                              data-testid={`bundle-badge-${box.box_id}`}
                            >
                              Direct Ship
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-[#5A5F6E]">{box.pick_slip_number || '-'}</td>
                      <td className="px-4 py-3 text-[#5A5F6E]">{box.customer || '-'}</td>
                      <td className="px-4 py-3 text-center">{box.items?.length || 0}</td>
                      <td className="px-4 py-3 text-center font-medium text-[#2D5F4F]">{box.total_qty}</td>
                      <td className="px-4 py-3 text-[#5A5F6E]">{box.packer_name || '-'}</td>
                      <td className="px-4 py-3 text-[#5A5F6E]">{formatDate(box.packed_at)}</td>
                      <td className="px-4 py-3 text-center">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadSlip(box)}
                          disabled={downloading === box.box_id}
                          data-testid={`bundle-download-${box.box_id}`}
                        >
                          {downloading === box.box_id ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Download className="w-4 h-4 mr-1" />
                              Reprint
                            </>
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-[#E5DFD0]">
              {boxes.map((box, idx) => (
                <div key={`${box.source}-${box.box_id}-${idx}`} className="p-4" data-testid={`bundle-card-${box.box_id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <div className="font-semibold text-[#1A1F2E] text-lg">{box.box_number}</div>
                        {box.source === 'inhouse_ship' && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-700 border border-indigo-200">
                            Direct Ship
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-[#5A5F6E]">{box.pick_slip_number || '-'}</div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadSlip(box)}
                      disabled={downloading === box.box_id}
                      data-testid={`bundle-download-mobile-${box.box_id}`}
                    >
                      {downloading === box.box_id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Download className="w-4 h-4 mr-1" />
                          Reprint
                        </>
                      )}
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center text-[#5A5F6E]">
                      <User className="w-4 h-4 mr-2" />
                      {box.customer || '-'}
                    </div>
                    <div className="flex items-center text-[#5A5F6E]">
                      <Package className="w-4 h-4 mr-2" />
                      {box.total_qty} pcs
                    </div>
                    <div className="flex items-center text-[#5A5F6E]">
                      <FileText className="w-4 h-4 mr-2" />
                      {box.items?.length || 0} items
                    </div>
                    <div className="flex items-center text-[#5A5F6E]">
                      <Calendar className="w-4 h-4 mr-2" />
                      {formatDate(box.packed_at)}
                    </div>
                  </div>
                  
                  <div className="mt-2 text-xs text-[#5A5F6E]">
                    Packed by: {box.packer_name || '-'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default BundleHistory;
